import { getIn, setIn } from "formik";
import merge from "lodash.merge";
import { IEFormsPhonePrefill } from "csd.phoenix.models/EFormsPhonePrefill";
import { IPrefillData } from "../../../../../stores/formsStore";
import { markdownToHtml } from "../../../../../utils/markdownUtils";
import { FormikErrorsEx, groupErrorsKey, GroupErrors } from "../../models/validation";
import { IEFormsAddressPrefill } from "csd.phoenix.models/EFormsAddressPrefill";
import { IEFormsInsurancePrefill } from 'csd.phoenix.models/EFormsInsurancePrefill';
import { IFieldPartDef } from "../utils";
import { INhsPrFormBenefitFlags } from '../interfaces/NhsPrForm'
import { getLocale, isLocaleUK, isLocaleUS } from '../../../../../i18n';

const requiredFieldMessage = "Required Field";
let isDoNotOrHaveToPayNHSChargesSectionDirty = false;

export function createGroupErrorMessage(id: string, errorMessage: string | string[]): GroupErrors {
  // We add custom errors to the Formik `errors` collection so that form validation
  // continues to treat these similar to regular validation errors.
  return {
    [groupErrorsKey]: {
      [id]: markdownToHtml(errorMessage)
    }
  }
}


interface IEntityWithPhones {
  HomePhone?: IEFormsPhonePrefill;
  MobilePhone?: IEFormsPhonePrefill;
  WorkPhone?: IEFormsPhonePrefill;
}


function validatePhoneNumbers(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean,
  bindingRoot: string, message: string) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const homePhoneBinding = bindingRoot + ".HomePhone";
  const workPhoneBinding = bindingRoot + ".WorkPhone";
  const mobilePhoneBinding = bindingRoot + ".MobilePhone";

  const phoneNumberTypeExists = templateHasField(homePhoneBinding) && templateHasField(workPhoneBinding) && templateHasField(mobilePhoneBinding);
  // If the field doesn't exist on the form, then bypass the rest of the validation.
  if (!phoneNumberTypeExists)
    return errors;

  const phones = getIn(values, bindingRoot) as IEntityWithPhones;
  if (phones && (phones.HomePhone || phones.MobilePhone || phones.WorkPhone)) {
    const number = phones.HomePhone?.Number || phones.MobilePhone?.Number || phones.WorkPhone?.Number;
    if (!number) {
      return createGroupErrorMessage(bindingRoot + ".Phones", message)
    }
  }
  return errors;
}


function validateAddress(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean,
  bindingRoot: string, autoAddrCheck: boolean = false ) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const addressBindingExists = (templateHasField(bindingRoot ))

  // If the field doesn't exist on the form, then bypass the rest of the validation.
  if (!addressBindingExists)
    return errors;


  const address = (getIn(values, bindingRoot) || {}) as IEFormsAddressPrefill;
  const haveAddress = (address.AddressLine1 || address.AddressLine2 || address.City
    || address.StateProvinceCode || address.PostalZipCode) || autoAddrCheck;

  if (haveAddress) {
    if (!address.AddressLine1) {
      errors = setIn(errors, bindingRoot + ".AddressLine1", requiredFieldMessage)
    }
    if (!address.City) {
      errors = setIn(errors, bindingRoot + ".City", requiredFieldMessage)
    }
    if ((!address.StateProvinceCode) || (address.StateProvinceCode === undefined)) {
      errors = setIn(errors, bindingRoot + ".StateProvinceCode", requiredFieldMessage)
    }
    if (!address.PostalZipCode) {
      errors = setIn(errors, bindingRoot + ".PostalZipCode", requiredFieldMessage)
    }
  }

  return errors;
}

function validateInsurance(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean,
  isPrimary: boolean) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const rootBinding = `Patient.${isPrimary ? "Primary" : "Secondary"}Insurance`;
  const insuranceBindingExists = templateHasField(rootBinding);

  // If the field doesn't exist on the form, then bypass the rest of the validation.
  if (!insuranceBindingExists)
    return errors;

  const insurance = (getIn(values, rootBinding) || {}) as IEFormsInsurancePrefill;
   const hasInsurance = (insurance.PayerName || insurance.PlanName || insurance.GroupNum || insurance.InsuredId
     || (insurance.Insured?.RelationshipCode !== undefined));


  if (hasInsurance) {
    const payerNameBinding = rootBinding + ".PayerName";
    const planNameBinding = rootBinding + ".PlanName";
    const insuredFirstNameBinding = rootBinding + ".Insured.FirstName";
    const insuredLastNameBinding = rootBinding + ".Insured.LastName";
    const insuredBirthdateBinding = rootBinding + ".Insured.Birthdate";
    const insuredIdBinding = rootBinding + ".InsuredId";
    const relationToPolicyHolderBinding = rootBinding + ".Insured.RelationshipCode";
    //const groupNumberBinding = rootBinding + ".GroupNum";
    const addressBinding = rootBinding + ".Address";

    if (!insurance.Insured?.FirstName)
      errors = setIn(errors, insuredFirstNameBinding, requiredFieldMessage);

    if (!insurance.Insured?.LastName)
      errors = setIn(errors, insuredLastNameBinding, requiredFieldMessage);

    if (!insurance.InsuredId)
      errors = setIn(errors, insuredIdBinding, requiredFieldMessage);

    if (!insurance.Insured?.Birthdate)
      errors = setIn(errors, insuredBirthdateBinding, requiredFieldMessage);

    if ((!insurance.Insured?.RelationshipCode) || (insurance.Insured?.RelationshipCode === undefined))
      errors = setIn(errors, relationToPolicyHolderBinding, requiredFieldMessage);

    if (!insurance.PlanName)
      errors = setIn(errors, planNameBinding, requiredFieldMessage);

    if (!insurance.PayerName)
      errors = setIn(errors, payerNameBinding, requiredFieldMessage);

    // if (!insurance.GroupNum)
    //   errors = setIn(errors, groupNumberBinding, requiredFieldMessage);

    errors = merge(errors, validateAddress(values, templateHasField, addressBinding, true));
    }

  return errors;
}

function validatePregnancyWeeks(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const flagBinding = "Patient.Health.IsPregnant";
  const weeksBinding = "Patient.Health.PregnancyWeeks";
  const flagBindingExists = templateHasField(flagBinding);

  // If the field doesn't exist on the form, then bypass the rest of the validation.
  if (!flagBindingExists)
    return errors;

  const weeksBindingExists = templateHasField(weeksBinding);

  if (weeksBindingExists) {
    const isPregnant = getIn(values, flagBinding, false);
    const weeks = Number.parseInt(getIn(values, weeksBinding, '0')) || null;

    if (isPregnant && !weeks) {
      errors = setIn(errors, weeksBinding, requiredFieldMessage);
    }
    else if (weeks && !isPregnant) {
      errors = setIn(errors, flagBinding, requiredFieldMessage);
    }
  }

  return errors;
}

function validateQuestions(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean) {
  let errors: FormikErrorsEx<IPrefillData> = {};
  const medicalQuestionnaireBinding = "Patient.MedicalQuestionnaire";
  const medicalQuestionnaireBindingExists = templateHasField(medicalQuestionnaireBinding);

  if(medicalQuestionnaireBindingExists) {
    if(!values.Patient?.MedicalQuestionnaire?.Questions)
      errors = setIn(errors, medicalQuestionnaireBinding, "Questions are required to be answered.");

    values.Patient?.MedicalQuestionnaire?.Questions?.forEach((q, i) => {
      // Commenting out for now as we aren't making every question required yet
      // Once Gender information is being migrated, will tweak this method to account for that
      // if(q && q.Answer == null)
      //   errors = setIn(errors, medicalQuestionnaireBinding + ".Questions[" + i + "]", "Please answer Yes or No");

      if(q && q.NoteOnYes && q.Answer) {
        if(!q.Note)
         errors = setIn(errors, medicalQuestionnaireBinding + ".Questions[" + i + "]", "Further explanation required");

        if(q.Note && q.Note.length > 250)
          errors = setIn(errors, medicalQuestionnaireBinding + ".Questions[" + i + "]", "Explanation must be no more than 250 characters in length");
      }
    });
  }
  return errors;
}

//function validate(values: IPrefillData, getFieldInfo: (fieldBinding: string) => IFieldPartDef | null): FormikErrorsEx<IPrefillData> {
function validate(values: IPrefillData, getFieldInfo: (fieldBinding: string) => IFieldPartDef | null,
  templateHasField: (bindingPrefix: string) => boolean): FormikErrorsEx<IPrefillData> {
  let errors: any;
  const locale = getLocale();

  //console.log("values", values);
  if (isLocaleUS(locale)) {

    errors = merge(
      validatePhoneNumbers(values, templateHasField, "Patient.Person", "At least one patient phone number is required"),
      validatePhoneNumbers(values, templateHasField, "Patient.EmergencyContact", "At least one emergency contact phone number is required"),
      validatePhoneNumbers(values, templateHasField, "Patient.ResponsibleParty", "At least one responsible party phone number is required"),
      //validateAddress(values, templateHasField, "Patient.ResponsibleParty.HomeAddress"),
      validateInsurance(values, templateHasField, true),
      validateInsurance(values, templateHasField, false),
      validatePregnancyWeeks(values, templateHasField),
      validateQuestions(values, templateHasField)
    );
  } else if (isLocaleUK(locale)) {

    errors = merge(
      //UK Practice Registration Form
      validatePhoneNumbers(values, templateHasField, "Patient.Person", "At least one patient phone number is required"),
      //UK PR validation
      validationOfPatientSignatureFields(values, getFieldInfo),
      validationOfEthnicGroup(values, getFieldInfo),
      validateBenefits(values, getFieldInfo, templateHasField),
      validatePatientOrGuardianSelections(values, templateHasField, "Patient.Benefits.Section2"),
      validateHaveToPayNHSChargesSection(values, templateHasField),
      validateDoNotHaveToPayNHSChargesSection(values, templateHasField),
      // Done last to allow for other validation.
      validateNHSChargesSectionWasFilledOut(values, templateHasField),
      validateQuestions(values, templateHasField)
    );
  }

  return errors;
}


function validateBenefits(values: IPrefillData, getFieldInfo: (fieldBinding: string) => IFieldPartDef | null,
  templateHasField: (bindingPrefix: string) => boolean) {
  let generalErrors: FormikErrorsEx<IPrefillData> = {};
  const flags = getPRBenefitElementFlags();

  let under18FieldInfo = getFieldInfo(flags.flagForUnder18);

  //if there is no under 18 field definition in the form assume that there are no NHS benefits sections
  if (!under18FieldInfo)
    return generalErrors;
  else {

    const errors = merge(
      generalErrors,
      validateNhsBenefitsSectionsA(values, flags),
      validateNhsBenefitsSectionsB(values, flags),
      validateNhsBenefitsSectionsC(values, flags),
      validateNhsBenefitsSectionsD(values, flags, templateHasField),
      validateNhsBenefitsAcrossSections(values, flags)
    );

    return errors;
  }
}


function validateNHSChargesCertificateSelection(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean,
  bindingRoot: string) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const flagNHSBeneficiary = bindingRoot + ".YesOrNo";
  const flagCertificateNumber = bindingRoot +".CertificateNumber";

  const isNHSBeneficiaryPresent = templateHasField(flagNHSBeneficiary);
  const isCertificateNumberPresent = templateHasField(flagCertificateNumber);

  if (!(isNHSBeneficiaryPresent && isCertificateNumberPresent))   //if either field is not present then do not attempt to validate them
    return errors;

  const isNHSBeneficiary = getIn(values, flagNHSBeneficiary, false);
  let certificateNumber = (getIn(values, flagCertificateNumber) || "") as string;
  certificateNumber = certificateNumber.trim();

  if ((!isNHSBeneficiary) && (certificateNumber !== "")) {
    errors = setIn(errors, flagNHSBeneficiary, requiredFieldMessage);
  }

  if ((certificateNumber === "") && (isNHSBeneficiary)) {
    errors = setIn(errors, flagCertificateNumber, requiredFieldMessage);
  }

  return errors;

}

function validateAwardedCoverageSelection(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  // If an award is selection then the person must fill out the person that benefits and dob or national insurance number.
  const flagAwardedCoverageType = "Patient.Benefits.Selection.NHSBeneficiaryHC2.AwardedCoverage";
  const flagAwardedCoverageBeneficiary = "Patient.Benefits.NHSBeneficiaryHC2.Name";
  const flagDoBOrNINumber = "Patient.Benefits.NHSBeneficiaryHC2.DoBOrNINumber";

  const isAwardedCoverageType = templateHasField(flagAwardedCoverageType);
  const isAwardedCoverageBeneficiary = templateHasField(flagAwardedCoverageBeneficiary);
  const isoBOrNINumber = templateHasField(flagDoBOrNINumber);

  if (!isAwardedCoverageType && !isAwardedCoverageBeneficiary && !isoBOrNINumber )   //if the field is not present then do not attempt to validate
    return errors;

  const awardedCoverageTypeSelection = (getIn(values, flagAwardedCoverageType) || []);
  const awardedCoverageBeneficiary = getIn(values, flagAwardedCoverageBeneficiary, false);
  const dobOrNINumber = getIn(values, flagDoBOrNINumber, false);

  if (awardedCoverageTypeSelection.length) {
    if (!awardedCoverageBeneficiary) {
      errors = setIn(errors, flagAwardedCoverageBeneficiary, requiredFieldMessage);
    }

    if (!dobOrNINumber) {
      errors = setIn(errors, flagDoBOrNINumber, requiredFieldMessage);
    }
  }

  return errors;

}

function validationOfPatientSignatureFields(values: IPrefillData, getFieldInfo: (fieldBinding: string) => IFieldPartDef | null) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const flagBenefitsSignedFor = "Patient.Benefits.SignedFor.Name";
  const flagPatientSignedFor = "Patient.SignedFor.Name";

  let fieldInfo = getFieldInfo(flagBenefitsSignedFor);

  if (fieldInfo) {
    errors = dependantStringFieldCheck(flagBenefitsSignedFor, "Patient.Benefits.SignedFor.RelationshipCode", values, errors);
  }

  fieldInfo = getFieldInfo(flagPatientSignedFor);
  if (fieldInfo) {
    errors = dependantStringFieldCheck(flagPatientSignedFor, "Patient.SignedFor.RelationshipCode", values, errors);
  }

  return errors;

}


//Simple function that checks if the first field is set, and if it is ensures that the second field is populated
//Assumes that both values are strings
function dependantStringFieldCheck(primaryField: string, dependantfield: string, values: IPrefillData, errors: FormikErrorsEx<IPrefillData>) {
  const primaryFieldValue = getIn(values, primaryField);
  if (primaryFieldValue) {
    const dependantFieldValue = getIn(values, dependantfield, '');
    if (dependantFieldValue.length === 0) {
      errors = setIn(errors, dependantfield, requiredFieldMessage);
    }
  }
  return errors;
}

function validationOfEthnicGroup(values: IPrefillData, getFieldInfo: (fieldBinding: string) => IFieldPartDef | null) {
  let errors: FormikErrorsEx<IPrefillData> = {};


  const flagEthnicity = "Patient.Person.EthnicityTypeDescription";
  const flagEthnicityDeclined = "Patient.Person.EthnicityTypeDeclined";
  //check that the two items are in the form definition
  let ethnicityField = getFieldInfo(flagEthnicity);
  let ethnicityDeclinedField = getFieldInfo(flagEthnicityDeclined);

  //Only perform the validation if both fields are present on the form
  if (!(ethnicityField && ethnicityDeclinedField))
    return errors;

  let grouperrors = {};
  const ethnicityValue = getIn(values, flagEthnicity);
  //ethnicity being unset can either be undefined or be an empty array
  const ethnicityValueSet = !!(ethnicityValue && ethnicityValue.length > 0);
  const ethnicityDeclinedSet = !!getIn(values, flagEthnicityDeclined);

  //Check that only one option is selected
  if (ethnicityValueSet && ethnicityDeclinedSet) {
    grouperrors = createGroupErrorMessage("Patient.Person.Ethnicity.GlobalErrorMessage", 'Select one option only.')
  }
  else if (!(ethnicityValueSet || ethnicityDeclinedSet)) {
    //no options selected
    grouperrors = createGroupErrorMessage("Patient.Person.Ethnicity.GlobalErrorMessage", 'Select ethnic origin OR declined option.')
  }
  let mergederrors = merge(errors, grouperrors)
  return mergederrors;
}

//Check that values of section A of the benefits are filled in correctly
function validateNhsBenefitsSectionsA(values: IPrefillData, flags: INhsPrFormBenefitFlags) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const isUnder18 = getIn(values, flags.flagForUnder18, false);
  const isInEducationAnd18 = getIn(values, flags.flagForInEducationAnd18, false);
  const isPregnant = getIn(values, flags.flagForIsPregnant, false);
  const hadBabyInLastYear = getIn(values, flags.flagForHadBabyInLastYear, false);
  const isIncarcerated = getIn(values, flags.flagForPatientIncarcerated, false);

  //convoluted check to ensure that only one item has been selected
  if ((isUnder18 && isInEducationAnd18) || (isUnder18 && isPregnant) || (isUnder18 && hadBabyInLastYear) || (isUnder18 && isIncarcerated)
    || (isInEducationAnd18 && isPregnant) || (isInEducationAnd18 && hadBabyInLastYear) || (isInEducationAnd18 && isIncarcerated)
    || (isPregnant && hadBabyInLastYear) || (isPregnant && isIncarcerated)
    || (hadBabyInLastYear && isIncarcerated)) {
    errors = setIn(errors, flags.flagForUnder18, 'Please only select one exemption category');
  }

  if (isInEducationAnd18) {
    const educationEstablishment = getIn(values, flags.flagForEducationEstablishment, '');
    if (educationEstablishment.length === 0)
      errors = setIn(errors, flags.flagForEducationEstablishment, requiredFieldMessage);
  }

  if (isPregnant || hadBabyInLastYear) {
    const nhsMaternityCardNumber = getIn(values, flags.flagForNhsMaternityCardNumber, '');
    if (nhsMaternityCardNumber.length === 0)
      errors = setIn(errors, flags.flagForNhsMaternityCardNumber, requiredFieldMessage);
    const babyDueBornDate = getIn(values, flags.flagForBabyDueBornDate, '');
    if (babyDueBornDate.length === 0)
      errors = setIn(errors, flags.flagForBabyDueBornDate, requiredFieldMessage);
  }
  return errors;
}

//Check that values of section B of the benefits are filled in correctly
function validateNhsBenefitsSectionsB(values: IPrefillData, flags: INhsPrFormBenefitFlags) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const claimingIncomeSupport = getIn(values, flags.flagForIncomeSupport, false);
  const claimingJobseekersAllowance = getIn(values, flags.flagForJobseekersAllowance, false);
  const claimingCreditGuarantee = getIn(values, flags.flagForCreditGuarantee, false);
  const claimingIncomeRelatedAllowance = getIn(values, flags.flagForIncomeRelatedAllowance, false);
  const claimingUniversalCredit = getIn(values, flags.flagForUniversalCredit, false);


  //convoluted check to ensure that only one item has been selected
  if ((claimingIncomeSupport && claimingJobseekersAllowance) || (claimingIncomeSupport && claimingCreditGuarantee) || (claimingIncomeSupport && claimingIncomeRelatedAllowance) || (claimingIncomeSupport && claimingUniversalCredit)
    || (claimingJobseekersAllowance && claimingCreditGuarantee) || (claimingJobseekersAllowance && claimingIncomeRelatedAllowance) || (claimingJobseekersAllowance && claimingUniversalCredit)
    || (claimingCreditGuarantee && claimingIncomeRelatedAllowance) || (claimingCreditGuarantee && claimingUniversalCredit)
    || (claimingIncomeRelatedAllowance && claimingUniversalCredit)) {
    errors = setIn(errors, flags.flagForIncomeSupport, 'Please only select one exemption category');
  }

  //check if claiming any of the section B benefits
  if (claimingIncomeSupport || claimingJobseekersAllowance || claimingCreditGuarantee || claimingIncomeRelatedAllowance || claimingUniversalCredit) {
    const nhsBenefitsDoB = getIn(values, flags['flagForBenefitsDoB'], '');
    if (nhsBenefitsDoB.length === 0)
      errors = setIn(errors, flags.flagForBenefitsDoB, requiredFieldMessage);
    const nhsPersonReceiving = getIn(values, flags.flagForPersonReceiving, '');
    if (nhsPersonReceiving.length === 0)
      errors = setIn(errors, flags.flagForPersonReceiving, requiredFieldMessage);
    const nhsNINumber = getIn(values, flags.flagForNINumber, '');
    if (nhsNINumber.length === 0)
      errors = setIn(errors, flags.flagForNINumber, requiredFieldMessage);
  }
  return errors;
}

//Check that values of section C of the benefits are filled in correctly
function validateNhsBenefitsSectionsC(values: IPrefillData, flags: INhsPrFormBenefitFlags) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const hasHC2Certificate = getIn(values, flags.flagForHc2Certificate, false);
  const isTaxExempt = getIn(values, flags.flagForNHSTaxExempt, false);

  //convoluted check to ensure that only one item has been selected
  if ((hasHC2Certificate && isTaxExempt)) {
    errors = setIn(errors, flags.flagForHc2Certificate, 'Please only select one exemption category');
  }

  if (hasHC2Certificate) {
    errors = dependantStringFieldCheck(flags.flagForHc2Certificate, flags.flagForHC2CertificateNumber, values, errors);
  }

  if (isTaxExempt) {
    errors = dependantStringFieldCheck(flags.flagForNHSTaxExempt, flags.flagForNHSTaxExemptNumber, values, errors);
  }

  return errors;
}

//Check that values of section D of the benefits are filled in correctly
function validateNhsBenefitsSectionsD(values: IPrefillData, flags: INhsPrFormBenefitFlags, templateHasField: (bindingPrefix: string) => boolean) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  errors = dependantStringFieldCheck(flags.flagForHaveHC3Certificate, flags.flagForHc3Amount, values, errors);
  errors = dependantStringFieldCheck(flags.flagForHaveHC3Certificate, flags.flagForHc3CertificateNumber, values, errors);
  return errors;
}


//Ensure that elements are consistent across all the benefits sections
function validateNhsBenefitsAcrossSections(values: IPrefillData, flags: INhsPrFormBenefitFlags) {
  let errors: FormikErrorsEx<IPrefillData> = {};
  //most of the benefits are mutually exclusive - you can only have one of these selected per form
  let countExclusiveOfBenefits = 0;
  if (getIn(values, flags.flagForUnder18, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForInEducationAnd18, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForIsPregnant, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForHadBabyInLastYear, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForPatientIncarcerated, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForIncomeSupport, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForJobseekersAllowance, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForIncomeRelatedAllowance, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForCreditGuarantee, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForUniversalCredit, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForHc2Certificate, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForHaveHC3Certificate, false)) countExclusiveOfBenefits += 1;
  if (getIn(values, flags.flagForNHSTaxExempt, false)) countExclusiveOfBenefits += 1;
  const hasUnder25OrOver60Benefit = getIn(values, flags.flagForUnder25OrOver60, false);
  let grouperrors = {};
  if (countExclusiveOfBenefits > 1) {
    grouperrors = createGroupErrorMessage("Patient.Benefits.GlobalErrorMessage", 'Please only select one type of exemption.')
  }
  if (countExclusiveOfBenefits > 0 || hasUnder25OrOver60Benefit) {
    if (!getIn(values, flags.flagForBenefitsSignature, false)) {
      errors = setIn(errors, flags.flagForBenefitsSignature, requiredFieldMessage);
    }
    if (!getIn(values, flags.flagForBenefitsSignatureDate, false)) {
      errors = setIn(errors, flags.flagForBenefitsSignatureDate, requiredFieldMessage);
    }

  }
  return merge(errors, grouperrors);
}

function getPRBenefitElementFlags(): INhsPrFormBenefitFlags {
  return {
    flagForHaveHC3Certificate: "Patient.Benefits.HaveHC3Certificate",
    flagForHc3Amount: "Patient.Benefits.Hc3Amount",
    flagForHc3CertificateNumber: "Patient.Benefits.Hc3CertificateNumber",
    flagForHc2Certificate: "Patient.Benefits.Hc2Certificate",
    flagForHC2CertificateNumber: "Patient.Benefits.HC2CertificateNumber",
    flagForNHSTaxExempt: "Patient.Benefits.NHSTaxExempt",
    flagForNHSTaxExemptNumber: "Patient.Benefits.NHSTaxExemptNumber",
    flagForIncomeSupport: "Patient.Benefits.hasIncomeSupport",
    flagForJobseekersAllowance: "Patient.Benefits.hasJobseekersAllowance",
    flagForCreditGuarantee: "Patient.Benefits.PensionCreditGuarantee",
    flagForIncomeRelatedAllowance: "Patient.Benefits.IncomeRelatedAllowance",
    flagForUniversalCredit: "Patient.Benefits.UniversalCredit",
    flagForUnder18: "Patient.Benefits.Under18",
    flagForInEducationAnd18: "Patient.Benefits.InEducationAnd18",
    flagForIsPregnant: "Patient.Benefits.IsPregnant",
    flagForHadBabyInLastYear: "Patient.Benefits.HadBabyInLastYear",
    flagForPatientIncarcerated: "Patient.Benefits.IsIncarcerated",
    flagForEducationEstablishment: "Patient.Benefits.EducationEstablishment",
    flagForNhsMaternityCardNumber: "Patient.Benefits.NhsMaternityCardNumber",
    flagForBabyDueBornDate: "Patient.Benefits.BabyDueBornDate",
    flagForBenefitsDoB: "Patient.Benefits.DateOfBirth",
    flagForPersonReceiving: "Patient.Benefits.PersonReceiving",
    flagForNINumber: "Patient.Benefits.NINumber",
    flagForBenefitsSignature: "Patient.Benefits.PatientDeclaration.Signature",
    flagForBenefitsSignatureDate: "Patient.Benefits.PatientDeclaration.SignatureDate",
    flagForUnder25OrOver60: "Patient.Benefits.Under25OrOver60"
  }
}

function validatePatientOrGuardianSelections(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean,
  bindingRoot: string, requiredPatientOrGuardianChk = true) {
  let errors: FormikErrorsEx<IPrefillData> = {};
  let grouperrors = {};

  const flagForIsPatient = bindingRoot + ".IsPatient";
  const flagForIsGuardian = bindingRoot + ".IsGuardian";
  const flagForSignedForName = bindingRoot + ".SignedFor.Name";

  const isPatientFieldPresent = templateHasField(flagForIsPatient);
  const isGuardianFieldPresent = templateHasField(flagForIsGuardian);

  if (!(isPatientFieldPresent && isGuardianFieldPresent))   //if either field is not present then do not attempt to validate them
    return errors;

  const isPatient = getIn(values, flagForIsPatient, false);
  const isGuardian = getIn(values, flagForIsGuardian, false);

  const bothItemsSelected = (isPatient && isGuardian);
  const neitherOptionSelected = !(isPatient || isGuardian);


  if (bothItemsSelected || (neitherOptionSelected && requiredPatientOrGuardianChk))  {
    grouperrors = createGroupErrorMessage(bindingRoot + ".GlobalErrorMessage", 'Please select patient or guardian.')
    errors = setIn(errors, flagForIsPatient, 'Please select either patient or guardian');
    errors = setIn(errors, flagForIsGuardian, 'Please select either patient or guardian');
  }

  const isSignedForNamePresent = templateHasField(flagForSignedForName);
  let signedForName = (getIn(values, flagForSignedForName) || "") as string;
  signedForName = signedForName.trim();

  if ((isGuardian && isSignedForNamePresent) && !bothItemsSelected) {
    if ((signedForName === "")) {
      errors = setIn(errors, flagForSignedForName, requiredFieldMessage);
    }
  }

  if ((neitherOptionSelected) && (signedForName !== "")) {
    grouperrors = createGroupErrorMessage(bindingRoot + ".GlobalErrorMessage", 'Please select patient or guardian.')
    errors = setIn(errors, flagForIsPatient, 'Please select either patient or guardian');
    errors = setIn(errors, flagForIsGuardian, 'Please select either patient or guardian');
  }

  if (isGuardian) {
    if (signedForName === "") {
      errors = setIn(errors, flagForSignedForName, requiredFieldMessage);
    }
  }


  return merge(errors, grouperrors);
}

function validateSignatureAndDate(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean,
  bindingRoot: string) {
  let errors: FormikErrorsEx<IPrefillData> = {};

  const flagSignature = bindingRoot + ".Signature";
  const flagSignatureDate = bindingRoot + ".SignatureDate";

  const isSignatureFieldPresent = templateHasField(flagSignature);
  const isSignatureDateFieldPresent = templateHasField(flagSignatureDate);

  //Need to do the signature check here because if there's a selection made, the fields are required.
  if (!isSignatureFieldPresent && !isSignatureDateFieldPresent) //if the fields is not present then do not attempt to validate
    return errors;

  const checkForSignature = getIn(values, flagSignature, false);
  const checkForSignatureDate = getIn(values, flagSignatureDate, false);


    if (!checkForSignature) {
      errors = setIn(errors, flagSignature, requiredFieldMessage);
    }

    if (!checkForSignatureDate) {
      errors = setIn(errors, flagSignatureDate, requiredFieldMessage);
    }

  return merge(errors);
}

// Before the form can be flagged as complete, the user must indicate whether they do or don't have to pay NHS charges.
// One of these sections have to be completed.
function validateNHSChargesSectionWasFilledOut(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean) {
  const hasTheseSectionsOnTemplate = templateHasField("Patient.Benefits.NHSBeneficiaryHC2")
    || templateHasField("Patient.Benefits.NHSBeneficiaryHC3");

  if (!isDoNotOrHaveToPayNHSChargesSectionDirty && hasTheseSectionsOnTemplate) {
    return createGroupErrorMessage("Patient.Benefits.NHS.GlobalErrorMessage",
      "Either the NHS Pay or Do Not Pay section must be completely filled out.")
  }
}

function validateHaveToPayNHSChargesSection(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean) {
  let errors: FormikErrorsEx<IPrefillData> = {};
  // If any of this section has value then the signature and date is required.
  const flagNHSChargeAcknowledgement = "Patient.Benefits.NHSBeneficiaryHC3.NHSChargeAcknowledgement";
  const flagNHSBeneficiaryHC3 = "Patient.Benefits.NHSBeneficiaryHC3.YesOrNo";
  const flagHC3CertificateNumber = "Patient.Benefits.NHSBeneficiaryHC3.CertificateNumber";
  const flagForIsPatient = "Patient.Benefits.NHSBeneficiaryHC3.IsPatient";
  const flagForIsGuardian ="Patient.Benefits.NHSBeneficiaryHC3.IsGuardian";
  const flagForSignedForName ="Patient.Benefits.NHSBeneficiaryHC3.SignedFor.Name";

  const nhsChargeAcknowledgement = templateHasField(flagNHSChargeAcknowledgement) &&  getIn(values, flagNHSChargeAcknowledgement);
  const nhsBeneficiaryHC3 = templateHasField(flagNHSBeneficiaryHC3) &&  getIn(values, flagNHSBeneficiaryHC3);
  const hc3CertificateNumber = templateHasField(flagHC3CertificateNumber) &&  getIn(values, flagHC3CertificateNumber);
  const isPatient = templateHasField(flagForIsPatient) &&  getIn(values, flagForIsPatient);
  const isGuardian = templateHasField(flagForIsGuardian) &&  getIn(values, flagForIsGuardian);
  const signedForName = templateHasField(flagForSignedForName) &&  getIn(values, flagForSignedForName);


  if (nhsChargeAcknowledgement || nhsBeneficiaryHC3 || hc3CertificateNumber || isPatient ||
    isGuardian || signedForName) {
    isDoNotOrHaveToPayNHSChargesSectionDirty = true;

    errors = validateNHSChargesCertificateSelection(values, templateHasField, "Patient.Benefits.NHSBeneficiaryHC3");

    errors = merge(errors, validatePatientOrGuardianSelections(values, templateHasField, "Patient.Benefits.NHSBeneficiaryHC3", false));

    errors = merge(errors, validateSignatureAndDate(values, templateHasField, "Patient.Benefits.NHSBeneficiaryHC3"));

    return errors;

  }

}

function validateDoNotHaveToPayNHSChargesSection(values: IPrefillData, templateHasField: (bindingPrefix: string) => boolean) {
  let errors: FormikErrorsEx<IPrefillData> = {};
  // If any of this section has value then the signature and date is required.
  const flagNHSChargeAcknowledgement = "Patient.Benefits.NHSBeneficiaryHC2.NoCharges";
  const flagNHSAwardedCoverage = "Patient.Benefits.Selection.NHSBeneficiaryHC2.AwardedCoverage";
  const flagNHSBeneficiaryHC2Name = "Patient.Benefits.NHSBeneficiaryHC2.Name";
  const flagNHSBeneficiaryHC2DoBOrNINumber = "Patient.Benefits.NHSBeneficiaryHC2.DoBOrNINumber";
  const flagNHSBeneficiaryHC2 = "Patient.Benefits.NHSBeneficiaryHC2.YesOrNo";
  const flagHC2CertificateNumber = "Patient.Benefits.NHSBeneficiaryHC2.CertificateNumber";
  const flagForIsPatient = "Patient.Benefits.NHSBeneficiaryHC2.IsPatient";
  const flagForIsGuardian = "Patient.Benefits.NHSBeneficiaryHC2.IsGuardian";
  const flagForSignedForName = "Patient.Benefits.NHSBeneficiaryHC2.SignedFor.Name";

  const nhsChargeAcknowledgement = templateHasField(flagNHSChargeAcknowledgement) && getIn(values, flagNHSChargeAcknowledgement);
  const nhsAwardedCoverage = templateHasField(flagNHSAwardedCoverage) && getIn(values, flagNHSAwardedCoverage);
  const nhsBeneficiaryHC2Name = templateHasField(flagNHSBeneficiaryHC2Name) && getIn(values, flagNHSBeneficiaryHC2Name);
  const nhsBeneficiaryHC2DoBOrNINumber = templateHasField(flagNHSBeneficiaryHC2DoBOrNINumber) && getIn(values, flagNHSBeneficiaryHC2DoBOrNINumber);
  const nhsBeneficiaryHC2 = templateHasField(flagNHSBeneficiaryHC2) && getIn(values, flagNHSBeneficiaryHC2);
  const hc2CertificateNumber = templateHasField(flagHC2CertificateNumber) && getIn(values, flagHC2CertificateNumber);
  const isPatient = templateHasField(flagForIsPatient) && getIn(values, flagForIsPatient);
  const isGuardian = templateHasField(flagForIsGuardian) && getIn(values, flagForIsGuardian);
  const signedForName = templateHasField(flagForSignedForName) && getIn(values, flagForSignedForName);

  if ((nhsAwardedCoverage && nhsAwardedCoverage.length) || nhsBeneficiaryHC2Name || (nhsChargeAcknowledgement && nhsChargeAcknowledgement.length) ||
    nhsBeneficiaryHC2DoBOrNINumber || nhsBeneficiaryHC2 || hc2CertificateNumber || isPatient || isGuardian || signedForName) {
    isDoNotOrHaveToPayNHSChargesSectionDirty = true;

    errors = validateNHSChargesCertificateSelection(values, templateHasField, "Patient.Benefits.NHSBeneficiaryHC2");

    errors = merge(errors, validatePatientOrGuardianSelections(values, templateHasField, "Patient.Benefits.NHSBeneficiaryHC2", false));

    errors = merge(errors, validateSignatureAndDate(values, templateHasField, "Patient.Benefits.NHSBeneficiaryHC2"));

    errors = merge(errors, validateAwardedCoverageSelection(values, templateHasField));

    return errors;

  }
}
export default validate;

